import { locale } from '../i18n';

const money = new Intl.NumberFormat(locale.intl, { style: 'currency', currency: 'ILS', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const moneyRound = new Intl.NumberFormat(locale.intl, { style: 'currency', currency: 'ILS', maximumFractionDigits: 0 });
const number = new Intl.NumberFormat(locale.intl);

export function fmtMoney(v: number | null | undefined, opts: { round?: boolean; dash?: string } = {}) {
  if (v == null || Number.isNaN(v)) return opts.dash ?? '—';
  const f = opts.round ? moneyRound : money;
  // In RTL text the minus of a negative amount drifts to the far side; wrap it
  // in a left-to-right isolate (U+2066 … U+2069) so it always reads "−₪1,234".
  return v < 0 ? `⁦−${f.format(-v)}⁩` : f.format(v);
}

export const fmtNumber = (v: number | null | undefined) => (v == null ? '—' : number.format(v));

/** 'YYYY-MM-DD' or ISO timestamp → DD/MM/YYYY (no timezone shift for plain dates). */
export function fmtDate(v: string | Date | null | undefined) {
  if (!v) return '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-');
    return `${d}/${m}/${y}`;
  }
  const d = typeof v === 'string' ? new Date(v) : v;
  // Built by hand: he-IL locale formatting uses dots, the rest of the app uses DD/MM/YYYY.
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export function fmtDateTime(v: string | Date | null | undefined) {
  if (!v) return '—';
  const d = typeof v === 'string' ? new Date(v) : v;
  return `${fmtDate(d)} ${d.toLocaleTimeString(locale.intl, { hour: '2-digit', minute: '2-digit' })}`;
}

const rtf = new Intl.RelativeTimeFormat(locale.intl, { numeric: 'auto' });

export function fmtRelative(v: string | Date | null | undefined) {
  if (!v) return '—';
  const d = typeof v === 'string' ? new Date(v) : v;
  const diff = (d.getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 45) return locale.justNow;
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 86400 * 7) return rtf.format(Math.round(diff / 86400), 'day');
  return fmtDate(d);
}

export function fmtMonth(yyyyMm: string) {
  const [y, m] = yyyyMm.split('-').map(Number);
  return new Date(y!, m! - 1, 1).toLocaleDateString(locale.intl, { month: 'long', year: 'numeric' });
}

export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function daysBetween(fromISO: string, toISO = todayISO()) {
  return Math.round((new Date(toISO).getTime() - new Date(fromISO).getTime()) / 86_400_000);
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('');
